import { regexSpecialCharacters } from './string-convert';

/** Constant for an empty regex pattern that matches nothing. */
export const EMPTY_REGEX = '^$';

/** Escapes special regex characters in a string using the shared regexSpecialCharacters set. */
export function escapeRegexChars(input: string): string {
    let result = '';
    for (const char of input) {
        if (regexSpecialCharacters.has(char)) {
            result += '\\' + char;
        } else {
            result += char;
        }
    }
    return result;
}

/**
 * Extracts an inline flag group (e.g. (?i), (?is), (?ism)) from the start of a regex.
 * Returns the individual flag characters and the remaining pattern, or null if no flag group.
 */
export function extractFlagGroup(regex: string): { flags: string; rest: string } | null {
    const match = regex.match(/^\(\?([a-z]+)\)/);
    if (!match) { return null; }
    return { flags: match[1], rest: regex.slice(match[0].length) };
}

/**
 * Ensures the given flag is present in the regex's inline flag group.
 * If no flag group exists, creates one. If the flag is already present, returns unchanged.
 */
export function ensureInlineFlag(regex: string, flag: string): string {
    const extracted = extractFlagGroup(regex);
    if (extracted) {
        if (extracted.flags.includes(flag)) { return regex; }
        return `(?${extracted.flags}${flag})${extracted.rest}`;
    }
    return `(?${flag})${regex}`;
}

/**
 * Removes the given flag from the regex's inline flag group.
 * If the flag group becomes empty after removal, it is stripped entirely.
 * If the flag is not present or there is no flag group, returns unchanged.
 */
export function removeInlineFlag(regex: string, flag: string): string {
    const extracted = extractFlagGroup(regex);
    if (!extracted || !extracted.flags.includes(flag)) { return regex; }
    const remaining = extracted.flags.replaceAll(flag, '');
    if (remaining.length === 0) { return extracted.rest; }
    return `(?${remaining})${extracted.rest}`;
}

/**
 * Builds a Python re-flavored regex from include/exclude string lists.
 * - Only includes [a, b]: ^(?:.*(?:a|b).*)$  (wildcard) or ^(?:a|b)$  (exact)
 * - Only excludes [x, y]: ^(?!.*x)(?!.*y).*$  (wildcard) or ^(?!x$)(?!y$).*$  (exact)
 * - Both: ^(?!.*x)(?!.*y)(?:.*(?:a|b).*)$
 * - Case insensitive: prepend (?i)
 * - Empty inputs returns EMPTY_REGEX ('^$')
 */
export function composeRegex(includes: string[], excludes: string[], caseInsensitive: boolean, includeWildcards: boolean = true, excludeWildcards: boolean = true): string {
    const filteredIncludes = includes.map(s => s.trim()).filter(s => s.length > 0);
    const filteredExcludes = excludes.map(s => s.trim()).filter(s => s.length > 0);

    if (filteredIncludes.length === 0 && filteredExcludes.length === 0) {
        return EMPTY_REGEX;
    }

    const prefix = caseInsensitive ? '(?i)' : '';

    const excludePart = filteredExcludes
        .map(s => `(?!${excludeWildcards ? '.*' : ''}${escapeRegexChars(s)}${excludeWildcards ? '' : '$'})`)
        .join('');

    let includePart = '';
    if (filteredIncludes.length > 0) {
        const alternatives = filteredIncludes.map(s => escapeRegexChars(s)).join('|');
        includePart = includeWildcards ? `(?:.*(?:${alternatives}).*)` : `(?:${alternatives})`;
    }

    if (filteredIncludes.length > 0 && filteredExcludes.length === 0) {
        return `${prefix}^${includePart}$`;
    }

    if (filteredIncludes.length === 0 && filteredExcludes.length > 0) {
        return `${prefix}^${excludePart}.*$`;
    }

    // Both includes and excludes
    return `${prefix}^${excludePart}${includePart}$`;
}

/**
 * Best-effort reverse parsing of the composer's own format.
 * Returns null for unrecognized patterns.
 */
export function decomposeRegex(regex: string): { includes: string[], excludes: string[], caseInsensitive: boolean, includeWildcards: boolean, excludeWildcards: boolean } | null {
    if (!regex || regex.length === 0 || regex === EMPTY_REGEX) {
        return { includes: [], excludes: [], caseInsensitive: true, includeWildcards: true, excludeWildcards: true };
    }

    let remaining = regex;
    let caseInsensitive = false;

    // Check for case insensitive flag — only recognize exactly (?i),
    // return null for unsupported flags to stay in Advanced mode
    const flagGroup = extractFlagGroup(remaining);
    if (flagGroup) {
        if (flagGroup.flags !== 'i') {
            return null;
        }
        caseInsensitive = true;
        remaining = flagGroup.rest;
    }

    // Must start with ^ and end with $
    if (!remaining.startsWith('^') || !remaining.endsWith('$')) {
        return null;
    }

    remaining = remaining.slice(1, -1); // remove ^ and $

    // Empty-like patterns such as ^$ or (?i)^$
    if (remaining.length === 0) {
        return { includes: [], excludes: [], caseInsensitive, includeWildcards: true, excludeWildcards: true };
    }

    // Try all combinations of include/exclude wildcard modes
    // Try wildcard-exclude combos first: the non-wildcard exclude pattern is less
    // specific and would also match wildcard excludes (e.g. (?!.*x) matched as content ".*x").
    const combos: [boolean, boolean][] = [[true, true], [false, true], [true, false], [false, false]];
    for (const [inclWc, exclWc] of combos) {
        const result = tryDecompose(remaining, inclWc, exclWc);
        if (result !== null) {
            return { ...result, caseInsensitive, includeWildcards: inclWc, excludeWildcards: exclWc };
        }
    }

    return null;
}

/** Try to decompose the inner pattern (between ^ and $) with the given wildcard modes. */
function tryDecompose(remaining: string, includeWildcards: boolean, excludeWildcards: boolean): { includes: string[], excludes: string[] } | null {
    let rest = remaining;

    // Parse negative lookaheads for excludes
    const excludes: string[] = [];
    const excludePattern = excludeWildcards
        ? /^\(\?!\.\*((?:[^()\\]|\\.)*?)\)/
        : /^\(\?!((?:[^()\\]|\\.)*?)\$\)/;

    while (true) {
        const match = rest.match(excludePattern);
        if (match) {
            excludes.push(unescapeRegexChars(match[1]));
            rest = rest.slice(match[0].length);
        } else {
            break;
        }
    }

    // Parse includes part
    const includes: string[] = [];

    if (rest === '.*') {
        // Only excludes, no includes - this is valid
        return { includes, excludes };
    }

    // Check for include pattern
    const includePattern = includeWildcards
        ? /^\(\?:\.\*\(\?:((?:[^()\\]|\\.|\|)*)\)\.\*\)$/
        : /^\(\?:((?:[^()\\]|\\.|\|)*)\)$/;
    const includeMatch = rest.match(includePattern);

    if (includeMatch) {
        const alternatives = splitByUnescapedPipe(includeMatch[1]);
        for (const alt of alternatives) {
            includes.push(unescapeRegexChars(alt));
        }
        return { includes, excludes };
    }

    // If remaining is empty and we have excludes, that's not a valid composer pattern
    if (rest.length === 0 && excludes.length > 0) {
        return null;
    }

    // Unrecognized pattern
    return null;
}

/** Unescapes regex special characters (reverses escapeRegexChars). */
function unescapeRegexChars(input: string): string {
    let result = '';
    let i = 0;
    while (i < input.length) {
        if (input[i] === '\\' && i + 1 < input.length) {
            result += input[i + 1];
            i += 2;
        } else {
            result += input[i];
            i++;
        }
    }
    return result;
}

/** Splits a string by unescaped pipe characters. */
function splitByUnescapedPipe(input: string): string[] {
    const parts: string[] = [];
    let current = '';
    let i = 0;
    while (i < input.length) {
        if (input[i] === '\\' && i + 1 < input.length) {
            current += input[i] + input[i + 1];
            i += 2;
        } else if (input[i] === '|') {
            parts.push(current);
            current = '';
            i++;
        } else {
            current += input[i];
            i++;
        }
    }
    parts.push(current);
    return parts;
}
